import {
  has, wrap, noop, first,
  negate, toLower, uniqueId,
  defaults, cloneDeep, isPlainObject
} from 'lodash-es'

import { trim, isTruthy, toObject, defaultTo } from '../src/utils/bits'

import Pull from '../src/utils/pull'
import setCss from '../src/utils/set-css'
import Emitter from '../src/utils/emitter'
import getSize from '../src/utils/get-size'
import SumEmitter from '../src/utils/sum-emitter'
import getElmsRange from '../src/utils/get-elms-range'
import AttrsObserver from '../src/utils/attrs-observer'
import attachGetters from '../src/utils/attach-getters'
import isBrightColor from '../src/utils/is-bright-color'
import extractRawElm from '../src/utils/extract-raw-elm'
import isWebSafeFont from '../src/utils/is-web-safe-font'
import googleFontLink from '../src/utils/google-font-link'
import viewportResize from '../src/utils/viewport-resize'
import ResizeDetector from '../src/utils/resize-detector'
import getBackgroundColor from '../src/utils/get-background-color'

import { PostHash } from '../src/gallery-modal-list/gallery-modal-list.post-hash'
import { embedModal } from '../src/utils/shared-constants'

import './embed-modal.less'
import Template from './embed-modal.ejs'

// export

const {
    HOR_ALIGN_CONFIG_DEFAULT,
    VER_ALIGN_CONFIG_DEFAULT,
    MIN_WIDTH_CONFIG_DEFAULT,
    MIN_HEIGHT_CONFIG_DEFAULT,
    MAX_WIDTH_CONFIG_DEFAULT,
    MAX_HEIGHT_CONFIG_DEFAULT
} = embedModal

const dirName = 'ggsGalleryDirective'
const template = Template()
const replacer = { dirName, template, controller }

export default { replacer }

// main

const QS_CONTENT = 'ggs-gallery-modal'
const QS_ALIGNER = '.ggs-embed-modal__vertical-aligner'
const QS_STRETCHER = '.ggs-embed-modal__stretcher'

const INLINE_ITEM_ATTR = 'data-embed-modal-inline-item'

const HOR_ALIGN_ATTR = 'data-embed-modal-hor-align'
const VER_ALIGN_ATTR = 'data-embed-modal-ver-align'

const HOR_ALIGN_LEFT = 'left'
const HOR_ALIGN_CENTER = 'center'
const HOR_ALIGN_RIGHT = 'right'

const VER_ALIGN_TOP = 'top'
const VER_ALIGN_MIDDLE = 'middle'
const VER_ALIGN_BOTTOM = 'bottom'

const MIN_WIDTH_ATTR = 'data-embed-modal-min-width'
const MIN_HEIGHT_ATTR = 'data-embed-modal-min-height'

const MAX_WIDTH_ATTR = 'data-embed-modal-max-width'
const MAX_HEIGHT_ATTR = 'data-embed-modal-max-height'

const SHARING_TINY_THRESHOLD = 50

function controller(
  $element,
  qh, $log, domh, $scope, rootQs,
  FormatPost, modalLayout, $rootElement,
  LazyResource, DefineMethod, DefineHandler,
  GalleryStylerNew, GSNApplyLinkTmpl, GSNApplyStyleTmpl) {

  'ngInject'

  setCss(getElmsRange($element, $rootElement), { overflow: 'visible' })

  const sign = `galleryMainPatch[uid=${uniqueId()}]`
  const $ctrl = $scope.$ctrl = attachGetters(this)
  const sizeAttrsObserver = AttrsObserver($rootElement, MIN_WIDTH_ATTR, MIN_HEIGHT_ATTR, MAX_WIDTH_ATTR, MAX_HEIGHT_ATTR)
  const alignAttrsObserver = AttrsObserver($rootElement, HOR_ALIGN_ATTR, VER_ALIGN_ATTR)
  const inlineItemAttrObserver = AttrsObserver($rootElement, INLINE_ITEM_ATTR)

  const parentRD = ResizeDetector($rootElement.parent())
  const resizeEmitter = SumEmitter(`${sign}::resize`,
    parentRD,
    viewportResize,
    sizeAttrsObserver)

  $ctrl.baseSize = calcBaseSize()
  $ctrl.getBaseWidth = $ctrl.getters.baseSize.width
  $ctrl.getBaseHeight = $ctrl.getters.baseSize.height

  modalLayout.setResizeEmitter(resizeEmitter)
  modalLayout.setBaseWidthGetter($ctrl.getBaseWidth)
  modalLayout.setBaseHeightGetter($ctrl.getBaseHeight)

  $ctrl.wrapM = DefineMethod(sign)
  $ctrl.wrapH = DefineHandler(sign)
  $ctrl.stylerNew = GalleryStylerNew($ctrl.ggsBootstrap.properties)

  const formatPost = FormatPost($ctrl.ggsBootstrap.properties)
  const styleHandlers = StyleHandlers()

  $ctrl.postHash = PostHash()
  $ctrl.postPull = Pull(`${sign}::pull`, 1)
  $ctrl.postMissed = false

  $ctrl.postVoteEmitter = Emitter(`${sign}::postVote`)
  $ctrl.wrapH('voteChangeHandler', noop)
  $ctrl.wrapH('modalDetachHandler', noop)
  $ctrl.wrapH('modalPrevRequestHandler', noop)
  $ctrl.wrapH('modalNextRequestHandler', noop)

  // own features

  $ctrl.wrapM('applyItem', () => {
    const item = cloneDeep(defaultTo(getInlineItem(), $ctrl.ggsBootstrap.displayed_item))
    if (!item) {
      $ctrl.postHash.currPost = null
      $ctrl.postPull.releaseAll()
      $ctrl.postMissed = true
      return $ctrl.ggsMainReadyCallback()
    } else {
      item.revision = 0
      return formatPost.below(item).then((post) => {
        $ctrl.postHash.currPost = post
        $ctrl.postPull.releaseAll()
        $ctrl.postPull.requestByIndex(0, post)
        $ctrl.postMissed = false
        return extModal.applyState($ctrl.postHash)
      })
    }
  })

  $ctrl.wrapM('setBaseSize', () => {
    $ctrl.baseSize = calcBaseSize()
  })

  $ctrl.wrapM('applyAlignConfig', () => {
    const alignerElm = domh.find($element, QS_ALIGNER)
    const horAlignConfig = getHorAlignConfig()
    const verAlignConfig = getVerAlignConfig()
    setCss(alignerElm, {
      textAlign: horAlignConfig,
      verticalAlign: verAlignConfig
    })

    const stretcherElm = domh.find($element, QS_STRETCHER)
    const stretcherWidth = horAlignConfig === HOR_ALIGN_LEFT ? 'auto' : '100%'
    const stretcherHeight = verAlignConfig === VER_ALIGN_TOP ? 'auto' : '100%'
    setCss(getElmsRange(stretcherElm, $rootElement), {
      width: stretcherWidth,
      height: stretcherHeight
    })
  })

  $ctrl.wrapM('applySlideIndex', () => {
    return extModal.nthSlide($ctrl.ggsBootstrap.properties.shared_slide_index)
  })

  $ctrl.wrapM('layoutSharingTooltip', () => {
    const contentElm = domh.find($element, QS_CONTENT)
    const viewportWidth = viewportResize.getWidth()
    const contentWidth = getSize(contentElm).width
    const baseWidth = $ctrl.getBaseWidth()
    const minOffset = Math.min(viewportWidth - contentWidth, baseWidth - contentWidth)
    const useTinyLayout = minOffset < SHARING_TINY_THRESHOLD
    $ctrl.ggsBootstrap.properties.override('sharing_tooltip_force_tiny', useTinyLayout)
  })

  $ctrl.wrapH('resizeHandler',
    $ctrl.setBaseSize)

  $ctrl.wrapH('inlineItemChangeHandler',
    $ctrl.applyItem)

  $ctrl.wrapH('alignConfigChangeHandler',
    $ctrl.applyAlignConfig)

  $ctrl.wrapH('viewportResizeHandler',
    $ctrl.layoutSharingTooltip)

  // <ggs-gallery-modal/> handlers

  const extModal = LazyResource(
    'hookPrev',
    'hookNext',
    'nthSlide',
    'lastSlide',
    'firstSlide',
    'applyState')

  $ctrl.wrapH('modalAttachHandler', (
    extModalHookPrev,
    extModalHookNext,
    extModalNthSlide,
    extModalLastSlide,
    extModalFirstSlide,
    extModalApplyState) => {

    extModal.hookPrev.allow(extModalHookPrev)
    extModal.hookNext.allow(extModalHookNext)
    extModal.nthSlide.allow(extModalNthSlide)
    extModal.lastSlide.allow(extModalLastSlide)
    extModal.firstSlide.allow(extModalFirstSlide)
    extModal.applyState.allow(extModalApplyState)
  })

  $ctrl.wrapH('modalReadyHandler',
    qh.makeJoin(
      $ctrl.layoutSharingTooltip,
      qh.makeIf(
        isTruthy,
        qh.makeOver(
          $ctrl.applySlideIndex,
          $ctrl.ggsMainReadyCallback))))

  $ctrl.wrapH('modalResizeHandler',
    $ctrl.layoutSharingTooltip)

  // <ggs-vote-frame/> handlers

  $ctrl.wrapH('voteCountHideHandler', () => {
    $ctrl.ggsBootstrap.properties.override('show_vote_count', false)
  })

  $ctrl.wrapH('votingDisallowHandler', () => {
    $ctrl.ggsBootstrap.properties.override('allow_voting', false)
    $ctrl.ggsBootstrap.properties.override('show_vote_count', false)
  })

  // $onInit

  $ctrl.wrapM('setupProps', () => {
    const bgColor = getBackgroundColor($element)
    const bgBright = isBrightColor(bgColor)
    // gallery-branding
    $ctrl.ggsBootstrap.properties.override('modals_bg_show', true)
    $ctrl.ggsBootstrap.properties.override('modals_bg_desktop_color', bgColor)
    $ctrl.ggsBootstrap.properties.override('modals_bg_desktop_opacity', 1)
    $ctrl.ggsBootstrap.properties.override('branding_footer_force_show', true)
    $ctrl.ggsBootstrap.properties.override('modal_attach_branding_footer', true)
    // gallery-empty
    $ctrl.ggsBootstrap.properties.override('body_background_show', true)
    $ctrl.ggsBootstrap.properties.override('body_background_color', bgColor)
    $ctrl.ggsBootstrap.properties.override('body_bg_color_opacity', 1)
    // gallery-modal's controls
    $ctrl.ggsBootstrap.properties.override('modals_show_close_button', false)
    $ctrl.ggsBootstrap.properties.override('modal_show_switch_buttons', false)
    // gallery-modals' view
    $ctrl.ggsBootstrap.properties.override('modal_show_shadow', bgBright)
    $ctrl.ggsBootstrap.properties.override('modal_show_placeholder', false)
  })

  $ctrl.wrapM('attachToStyler',
    qh.makeOver(
      $ctrl.stylerNew.init,
      qh.makeCatch(
        wrap(styleHandlers, $ctrl.stylerNew.reg),
        wrap(`${sign}#attachToStyler: error halted:`, $log.error))))

  $ctrl.wrapM('attachToEmitters',
    qh.makeJoin(
      wrap($ctrl.resizeHandler, resizeEmitter.on),
      wrap($ctrl.viewportResizeHandler, viewportResize.on),
      wrap($ctrl.inlineItemChangeHandler, inlineItemAttrObserver.on),
      wrap($ctrl.alignConfigChangeHandler, alignAttrsObserver.on)))

  $ctrl.wrapH('$onInit',
    qh.makeJoin(
      $ctrl.setupProps,
      $ctrl.attachToStyler,
      $ctrl.attachToEmitters,
      $ctrl.layoutSharingTooltip,
      $ctrl.applyAlignConfig,
      $ctrl.applyItem))

  // $onDestroy

  $ctrl.wrapM('detachFromStyler', () => {
    return qh.join(
      $ctrl.stylerNew.release(styleHandlers),
      $ctrl.stylerNew.destroy())
  })

  $ctrl.wrapM('detachFromEmitters',
    qh.makeJoin(
      wrap($ctrl.resizeHandler, resizeEmitter.off),
      wrap($ctrl.viewportResizeHandler, viewportResize.off),
      wrap($ctrl.inlineItemChangeHandler, inlineItemAttrObserver.off),
      wrap($ctrl.alignConfigChangeHandler, alignAttrsObserver.off)))

  $ctrl.wrapH('$onDestroy',
    qh.makeJoin(
      $ctrl.detachFromStyler,
      $ctrl.detachFromEmitters))

  // others

  function getInlineItem() {
    const inlineItem = toObject(getAttr(INLINE_ITEM_ATTR))
    if (isPlainObject(inlineItem)) {
      return defaults(inlineItem, { id: -1 })
    } else {
      return null
    }
  }

  function getHorAlignConfig() {
    const letter = first(getLowerAttr(HOR_ALIGN_ATTR, HOR_ALIGN_CONFIG_DEFAULT))
    const letter2config = {
      [first(HOR_ALIGN_LEFT)]:   HOR_ALIGN_LEFT,
      [first(HOR_ALIGN_CENTER)]: HOR_ALIGN_CENTER,
      [first(HOR_ALIGN_RIGHT)]:  HOR_ALIGN_RIGHT,
      [first(VER_ALIGN_TOP)]:    HOR_ALIGN_LEFT,
      [first(VER_ALIGN_MIDDLE)]: HOR_ALIGN_CENTER,
      [first(VER_ALIGN_BOTTOM)]: HOR_ALIGN_RIGHT
    }

    if (has(letter2config, letter)) {
      return letter2config[letter]
    }

    $log.error(`${sign}#getHorAlignConfig: "${getLowerAttr(HOR_ALIGN_ATTR)}" has wrong format!`)
    return HOR_ALIGN_CONFIG_DEFAULT
  }

  function getVerAlignConfig() {
    const letter = first(getLowerAttr(VER_ALIGN_ATTR, VER_ALIGN_CONFIG_DEFAULT))
    const letter2config = {
      [first(VER_ALIGN_TOP)]:    VER_ALIGN_TOP,
      [first(VER_ALIGN_MIDDLE)]: VER_ALIGN_MIDDLE,
      [first(VER_ALIGN_BOTTOM)]: VER_ALIGN_BOTTOM,
      [first(HOR_ALIGN_LEFT)]:   VER_ALIGN_TOP,
      [first(HOR_ALIGN_CENTER)]: VER_ALIGN_MIDDLE,
      [first(HOR_ALIGN_RIGHT)]:  VER_ALIGN_BOTTOM
    }

    if (has(letter2config, letter)) {
      return letter2config[letter]
    }

    $log.error(`${sign}#getVerAlignConfig: "${getLowerAttr(VER_ALIGN_ATTR)}" has wrong format!`)
    return VER_ALIGN_CONFIG_DEFAULT
  }

  function getMinWidthConfig() {
    return getLowerAttr(MIN_WIDTH_ATTR, MIN_WIDTH_CONFIG_DEFAULT)
  }

  function getMaxWidthConfig() {
    return getLowerAttr(MAX_WIDTH_ATTR, MAX_WIDTH_CONFIG_DEFAULT)
  }

  function getMinHeightConfig() {
    return getLowerAttr(MIN_HEIGHT_ATTR, MIN_HEIGHT_CONFIG_DEFAULT)
  }

  function getMaxHeightConfig() {
    return getLowerAttr(MAX_HEIGHT_ATTR, MAX_HEIGHT_CONFIG_DEFAULT)
  }

  function getLowerAttr(attrName, defValue) {
    return toLower(getAttr(attrName, defValue))
  }

  function getAttr(attrName, defValue) {
    return trim(defaultTo($rootElement.attr(attrName), defValue))
  }

  function calcBaseSize() {
    const sizerElm = makeSizerElm()

    const minWidthConfig = getMinWidthConfig()
    const maxWidthConfig = getMaxWidthConfig()

    const minHeightConfig = getMinHeightConfig()
    const maxHeightConfig = getMaxHeightConfig()

    const prntElm = extractRawElm($rootElement.parent())
    const rootElm = extractRawElm($rootElement)

    const prntPosition = prntElm.style.position
    const rootPosition = rootElm.style.position
    setCss(prntElm, 'position', 'relative')
    setCss(rootElm, 'position', 'absolute')

    setCss(sizerElm, 'display', 'block')
    setCss(sizerElm, {
      width: maxWidthConfig,
      height: maxHeightConfig,
      minWidth: minWidthConfig,
      minHeight: minHeightConfig
    })
    const size = getSize(sizerElm)
    setCss(sizerElm, 'display', 'none')

    setCss(prntElm, 'position', prntPosition)
    setCss(rootElm, 'position', rootPosition)

    return size
  }

  function makeSizerElm() {
    const parentElm = extractRawElm($rootElement.parent())
    if (parentElm.__ggsEmbedModalSizer__) {
      return parentElm.__ggsEmbedModalSizer__
    }

    const sizerElm = document.createElement('div')
    sizerElm.setAttribute('ggs-embed-modal-sizer', sign)
    setCss(sizerElm, {
      display: 'none',
      maxWidth: 'none',
      maxHeight: 'none',
      margin: 0,
      padding: 0,
      borderWidth: 0,
      outlineWidth: 0
    })

    parentElm.appendChild(sizerElm)
    parentElm.__ggsEmbedModalSizer__ = sizerElm
    return sizerElm
  }

  function StyleHandlers() {
    const OWNER_TAG = __filename

    // applyCustomStyles

    const applyCustomStyles = GSNApplyStyleTmpl({
      propKeys: 'custom_styles',
      ownerTag: OWNER_TAG,
      allowEmpty: false,
      allowFunc: domh.checkCssBrackets,
      styleFunc: domh.addRootQs
    })

    const BODY_FONT_NAME_PLH = ':font-family:'
    const BODY_FONT_NAME_TMPL = `
      ${rootQs} {
        font-family: '${BODY_FONT_NAME_PLH}', 'Lato', sans-serif;
      }`

    const applyBodyFontName = GSNApplyStyleTmpl({
      propKeys: 'body_font_name',
      ownerTag: OWNER_TAG,
      styleTmpl: BODY_FONT_NAME_TMPL,
      stylePlh: BODY_FONT_NAME_PLH
    })

    // applyBodyFontLink

    const applyBodyFontLink = GSNApplyLinkTmpl({
      propKeys: 'body_font_name',
      ownerTag: OWNER_TAG,
      linkFunc: googleFontLink,
      allowEmpty: false,
      allowFunc: negate(isWebSafeFont)
    })

    return [
      applyCustomStyles,
      applyBodyFontName,
      applyBodyFontLink
    ]
  }
}
